import { useEffect, useState, ChangeEvent } from "react";
import axios from "axios";
import dayjs, { Dayjs } from "dayjs";
import { API_BASE_PATH } from "../../environment";
import { toast } from "react-toastify";

export interface EducationFormData {
  id?: number;
  employee_id?: string;
  institution_name: string;
  course: string;
  start_date: string;
  end_date: string;
}

// Centralizing the Education API URL
const EDUCATION_API_URL = `${API_BASE_PATH}/education`;

// ====== Error Extractor ======
const extractErrorMessage = (error: any) => {
  const detail = error?.response?.data?.detail;
  let details = error?.response?.data?.details;
  const message = error?.response?.data?.message;

  if (typeof details === "string") {
    details = details.replace(/^\d+\s*:\s*/, ""); // strip numeric codes
  }

  return detail || details || message;
};

// 🔹 Hook now accepts token
export const useEducationInfo = (
  employeeId: string | null,
  token: string | null
) => {
  const [formDataEducation, setFormDataEducation] = useState<EducationFormData>(
    {
      institution_name: "",
      course: "",
      start_date: "",
      end_date: "",
    }
  );

  const [educationList, setEducationList] = useState<EducationFormData[]>([]);

  const getEducationInfo = async () => {
    if (!employeeId) return;
    try {
      const res = await axios.get(`${EDUCATION_API_URL}/${employeeId}`, {
        headers: {
          accept: "application/json",
          authorization: "Bearer " + token,
        },
      });

      // ✅ Ensure we always have an array
      setEducationList(
        Array.isArray(res.data)
          ? res.data
          : Array.isArray(res.data?.data)
          ? res.data.data
          : []
      );
    } catch (err: any) {
      console.error("❌ Error fetching education info", err);
      toast.error(extractErrorMessage(err));
      setEducationList([]); // Fallback to empty array on error
    }
  };

  useEffect(() => {
    getEducationInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeId, token]); // 🔹 Refetch if token changes

  const handleChangeEducation = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormDataEducation((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleDateChangeEducation = (
    name: "start_date" | "end_date",
    date: Dayjs | null
  ) => {
    if (date) {
      setFormDataEducation((prev) => ({
        ...prev,
        [name]: date.format("YYYY-MM-DD"),
      }));
    }
  };

  const handleSubmitEducation = async () => {
    if (!employeeId) {
      toast.error("Employee ID is missing.");
      return;
    }

    const { institution_name, course, start_date, end_date } =
      formDataEducation;
    if (!institution_name || !course || !start_date || !end_date) {
      toast.error("Please fill in all required fields.");
      return;
    }

    try {
      if (formDataEducation.id) {
        try {
          await axios.put(
            `${EDUCATION_API_URL}/${formDataEducation.id}`,
            {
              institution_name,
              course,
              start_date,
              end_date,
            },
            {
              headers: {
                Accept: "application/json",
                "Content-Type": "application/json",
                authorization: "Bearer " + token, // ✅ Added Bearer token
              },
            }
          );
          toast.success("Education information updated!");
        } catch (putError: any) {
          if (putError.response?.status === 404) {
            console.warn("Record not found, creating new instead.");
            await axios.post(
              EDUCATION_API_URL,
              {
                employee_id: employeeId,
                institution_name,
                course,
                start_date,
                end_date,
              },
              {
                headers: {
                  Accept: "application/json",
                  "Content-Type": "application/json",
                  authorization: "Bearer " + token, // ✅ Added Bearer token
                },
              }
            );
            toast.success("Education information added!");
          } else {
            throw putError;
          }
        }
      } else {
        await axios.post(
          EDUCATION_API_URL,
          {
            employee_id: employeeId,
            institution_name,
            course,
            start_date,
            end_date,
          },
          {
            headers: {
              Accept: "application/json",
              "Content-Type": "application/json",
              authorization: "Bearer " + token, // ✅ Added Bearer token
            },
          }
        );
        toast.success("Education information added!");
      }

      setFormDataEducation({
        institution_name: "",
        course: "",
        start_date: "",
        end_date: "",
      });

      await getEducationInfo();
    } catch (err: any) {
      console.error("❌ Error submitting education info:", err);
      toast.error(extractErrorMessage(err));
    }
  };

  const handleEditEducation = (record: EducationFormData) => {
    setFormDataEducation({ ...record });
  };

  return {
    formDataEducation,
    setFormDataEducation,
    educationList,
    handleChangeEducation,
    handleDateChangeEducation,
    handleSubmitEducation,
    handleEditEducation,
    getEducationInfo,
  };
};
